import { useId, useState } from "react";
import { StateMessage } from "@/components/StateMessage";
import { Icon } from "@/components/Icon";
import { RowActions } from "@/components/RowActions";
import { PunchLocationLine } from "@/components/PunchLocationLine";
import { usePendingTime } from "@/hooks/usePendingTime";
import { useJobCodes } from "@/hooks/useJobCodes";
import { demoApi, problemMessage } from "@/api/client";
import { formatDateShort, formatOfflineDelay } from "@/lib/format";
import type { Guid, JobCode, TimeEntry } from "@/api/types";
import "./TimeApprovalQueue.css";

/**
 * The owner's half of employee time: what the workers have sent in, and the decision on
 * each one.
 *
 * WHY THIS SITS ON THE PAYROLL SCREEN rather than getting a nav item of its own. Approving
 * hours is not an administrative errand — it is the step between "somebody says they
 * worked" and a figure that reaches a paycheck and Minnesota's quarterly wage detail.
 * Putting it next to the run makes that adjacency the layout instead of a thing to
 * remember, and follows the same reasoning AppShell.tsx gives for keeping Paid Leave and
 * Year-end as payroll sub-screens rather than a fifth and sixth destination.
 *
 * A REJECTION MUST CARRY A REASON. The API allows a bare rejection; this screen does not,
 * and the difference is deliberate. The server's job is to accept a valid decision; the
 * product's job is to stop a farmer sending "no" with no explanation to somebody who
 * believes they worked that day. That is where a payroll dispute starts, and the schema
 * says so in its own comment on `review_note`.
 */

/**
 * How far back the roll-up reaches.
 *
 * Matched to `TimeEndpoints.MaxBackdateDays` — the window a worker may enter time for is
 * exactly the window their employer needs to sweep, and any other number silently strands
 * rows at one end or the other.
 *
 * NOT `PAY_PERIOD_LABEL` from payroll-fixtures.ts, which is a DECLARED FIXTURE ("Aug 1–15",
 * tax year 2026) describing the demo's payroll run. These time entries are real rows with
 * real dates, and rolling them up against a fixed fictional fortnight would report zero
 * every time while looking like it worked. The control below states its window rather than
 * implying it.
 */
const APPLY_WINDOW_DAYS = 14;

type Pending = { kind: "none" } | { kind: "deciding"; id: Guid } | { kind: "applying" };

export function TimeApprovalQueue() {
  const { entries, status, error, reload } = usePendingTime();
  // ef-023 — the farm's own job codes, for the Allocate control's job-code pick-lists.
  // ACTIVE ONLY: an archived code is refused by the allocations PUT itself
  // (TimeEndpoints.cs), so offering one in the picker would be a control that always
  // fails when used. This hook's own `reload` is never called here — creating a job code
  // happens on a different screen (JobCodes.tsx), and this queue simply reads whatever is
  // active at the moment it loaded; a farmer who adds one mid-review refreshes to see it,
  // same as every other list in this app.
  const { active: activeJobCodes } = useJobCodes();

  const [busy, setBusy] = useState<Pending>({ kind: "none" });
  const [actionError, setActionError] = useState<string | null>(null);
  /** Set once anything has been approved in this sitting — what reveals the roll-up. */
  const [approvedAny, setApprovedAny] = useState(false);
  const [applyResult, setApplyResult] = useState<string | null>(null);
  /** Which row is currently being rejected, i.e. showing its note field. */
  const [rejecting, setRejecting] = useState<Guid | null>(null);

  async function decide(id: Guid, approved: boolean, note?: string): Promise<void> {
    setBusy({ kind: "deciding", id });
    setActionError(null);
    setApplyResult(null);
    try {
      await demoApi.reviewTime(id, approved ? { approved } : { approved, note });
      if (approved) setApprovedAny(true);
      setRejecting(null);
    } catch (err) {
      setActionError(
        problemMessage(err, "That decision didn't go through — the queue has been reloaded."),
      );
    } finally {
      setBusy({ kind: "none" });
      // Always, including after a failure: a 409 means somebody else decided this row, and
      // the only useful next state is a queue that reflects that rather than one still
      // offering buttons for a decision that has already been made.
      await reload();
    }
  }

  async function applyHours(): Promise<void> {
    setBusy({ kind: "applying" });
    setActionError(null);
    try {
      const to = new Date();
      const from = new Date();
      from.setDate(from.getDate() - APPLY_WINDOW_DAYS);
      const result = await demoApi.applyApprovedHours({
        from: from.toISOString().slice(0, 10),
        to: to.toISOString().slice(0, 10),
      });
      setApplyResult(
        result.employeesUpdated === 0
          ? "No approved hours in that window, so nobody's total changed."
          : `Hours updated for ${result.employeesUpdated} ${
              result.employeesUpdated === 1 ? "employee" : "employees"
            }. Reload the roster to see it.`,
      );
      setApprovedAny(false);
    } catch (err) {
      setActionError(problemMessage(err, "Could not apply the approved hours."));
    } finally {
      setBusy({ kind: "none" });
    }
  }

  return (
    // data-state is the tester's ground truth for this section (rules/ui-breadcrumbs §1):
    // loading / error / empty / ready, off the DOM rather than inferred from whether some
    // sentence happens to be rendered.
    <section
      className="time-queue"
      aria-labelledby="time-queue-heading"
      data-state={status === "ready" && (entries?.length ?? 0) === 0 ? "empty" : status}
    >
      <h2 id="time-queue-heading" className="payroll-page__card-title">
        Time to approve
      </h2>

      {status === "loading" && <StateMessage tone="loading" title="Loading submitted time…" />}

      {status === "error" && (
        <StateMessage
          tone="error"
          title="Could not load the time-approval queue."
          detail={`${error ?? ""} Nothing is shown below — an empty queue and an unreachable one look identical, and only one of them means there is nothing to do.`}
        />
      )}

      {status === "ready" && entries !== null && entries.length === 0 && (
        <StateMessage
          tone="empty"
          title="Nothing waiting on you."
          detail="Hours your team clocks or enters land here for approval before they reach payroll."
        />
      )}

      {status === "ready" && entries !== null && entries.length > 0 && (
        <ul className="time-queue__list ruled-rows" aria-labelledby="time-queue-heading">
          {entries.map((entry) => (
            <QueueRow
              key={entry.id}
              entry={entry}
              jobCodes={activeJobCodes ?? []}
              busy={busy.kind === "deciding" && busy.id === entry.id}
              disabled={busy.kind !== "none"}
              rejecting={rejecting === entry.id}
              onStartReject={() => setRejecting(entry.id)}
              onCancelReject={() => setRejecting(null)}
              onDecide={decide}
              onAllocated={reload}
            />
          ))}
        </ul>
      )}

      {actionError && (
        <p className="time-queue__error" role="alert">
          {actionError}
        </p>
      )}

      {/* Offered only after something was approved in this sitting. A standing button
          would invite a farmer to press it on a screen where nothing had changed and get
          a "0 employees updated" that reads like a failure. */}
      {approvedAny && (
        <div className="time-queue__apply">
          <p className="time-queue__apply-copy">
            Approved hours are recorded but not yet on the roster. Add the last{" "}
            {APPLY_WINDOW_DAYS} days&apos; approved hours to each employee&apos;s current
            period — this is the Hours column Minnesota&apos;s quarterly wage detail asks
            for.
          </p>
          <button
            type="button"
            className="btn btn--primary time-queue__apply-btn"
            disabled={busy.kind !== "none"}
            onClick={() => void applyHours()}
          >
            {busy.kind === "applying" ? "Applying…" : "Add approved hours to this period"}
          </button>
        </div>
      )}

      {applyResult && (
        <p className="time-queue__result" role="status">
          <Icon name="check" size={15} strokeWidth={2.4} />
          {applyResult}
        </p>
      )}
    </section>
  );
}

/**
 * Whether the WORKER agreed these hours, shown before the farmer decides.
 *
 * THIS IS THE WHOLE POINT OF SIGNING. A signature filed somewhere the approver never
 * looks is a record; a signature on the row they are approving is evidence they acted on.
 * So it sits in the queue, next to the name, and it is read BEFORE the Approve button
 * rather than reported afterwards.
 *
 * ⚠️ IT IS NOT AN APPROVAL, and the wording keeps the two apart deliberately: "Signed" is
 * the worker's act, "Approve" is the farmer's, and the product keeps both. Nothing here
 * says "H-2A" or "compliant" — 20 CFR 655.122(j) has no signing requirement (see
 * db/migrations/0018_timesheet_signature.sql).
 *
 * NOT SIGNED IS RENDERED, NOT OMITTED. A missing chip and an unsigned row would look
 * identical, and the farmer would read silence as "fine". The absence is the finding.
 */
function SignedChip({ entry }: { entry: TimeEntry }) {
  if (entry.signedAt === null) {
    return <span className="time-queue__signed time-queue__signed--no">Not signed</span>;
  }

  if (entry.signatureStale) {
    return (
      <span className="time-queue__signed time-queue__signed--stale">
        Signed {formatDateShort(entry.signedAt.slice(0, 10))} · hours changed since
      </span>
    );
  }

  return (
    <span className="time-queue__signed time-queue__signed--yes">
      Signed {formatDateShort(entry.signedAt.slice(0, 10))}
    </span>
  );
}

/** Ground truth for a tester (rules/ui-breadcrumbs §1) — never inferred from the chip's text. */
function signedState(entry: TimeEntry): "no" | "stale" | "yes" {
  if (entry.signedAt === null) return "no";
  return entry.signatureStale ? "stale" : "yes";
}

/**
 * ef-018 — the farmer sees a punch's clock times came from the PHONE, not the server, and
 * how much later that claim actually arrived. Sits with the date/source line it explains,
 * not with the hours: the hours figure is unaffected by any of this, it is the TRUST in the
 * two timestamps behind it that changes.
 *
 * data-state="offline" is the tester's ground truth (rules/ui-breadcrumbs §1) — an online
 * row renders nothing here at all rather than a "false" state, so a query for the marker
 * only ever finds real offline rows.
 *
 * @ui-check: `receivedAt` is only meaningful alongside `recordedOffline` (api/types.ts) —
 * guarded here so a row that is somehow offline with no receipt timestamp renders nothing
 * rather than a delay computed against `null`.
 */
function OfflineProvenance({ entry }: { entry: TimeEntry }) {
  if (!entry.recordedOffline || entry.receivedAt === null) return null;

  const reference = entry.endedAt ?? entry.startedAt;
  if (reference === null) return null;

  return (
    <span className="time-queue__offline" data-state="offline">
      Recorded offline · {formatOfflineDelay(entry.receivedAt, reference)}
    </span>
  );
}

function QueueRow({
  entry,
  jobCodes,
  busy,
  disabled,
  rejecting,
  onStartReject,
  onCancelReject,
  onDecide,
  onAllocated,
}: {
  entry: TimeEntry;
  jobCodes: JobCode[];
  busy: boolean;
  disabled: boolean;
  rejecting: boolean;
  onStartReject: () => void;
  onCancelReject: () => void;
  onDecide: (id: Guid, approved: boolean, note?: string) => Promise<void>;
  onAllocated: () => Promise<void>;
}) {
  const [note, setNote] = useState("");
  const canReject = note.trim().length > 0;

  const [allocateOpen, setAllocateOpen] = useState(false);
  const isAllocated = entry.allocations.length > 0;

  return (
    <li
      className="time-queue__row"
      data-signed={signedState(entry)}
      // ef-023 — ground truth for a tester (ui-breadcrumbs §1): read off the DOM rather
      // than inferred from whether a split summary happens to be rendered.
      data-state={isAllocated ? "allocated" : undefined}
    >
      <span className="time-queue__who">
        <span className="time-queue__name">{entry.employeeName}</span>
        <span className="time-queue__number">#{entry.employeeNumber}</span>
        <SignedChip entry={entry} />
      </span>

      <span className="time-queue__when">
        <span className="time-queue__date">{formatDateShort(entry.workDate)}</span>
        <span className="time-queue__source">
          {/* WHICH WAY it was recorded is shown because it changes what the farmer is
              being asked to trust: a punch is two server timestamps, a manual entry is
              somebody's word. The payable figure is identical either way, which is the
              point of the shared table — but the person approving it should know. */}
          {/*
            WHERE, not whether. This is the row the farmer decides on, and "was he at the
            shop when he said he was" is the question the coordinates were captured for —
            a boolean saying a location exists answered none of it.

            THE LOCATION LINE REPLACES THE SOURCE WORD rather than following it. Both
            rendered together read "Clocked in/out · Clocked in at 44.4469, -95.7889" —
            the same fact stated twice, three words apart. Found by opening the screen;
            no assertion in the suite could see it, because every string it checks for was
            present and correct.

            A punch with no fix still needs the word, so this is a swap and not a deletion:
            without a location the row must still say how the time was recorded.
          */}
          {entry.location === null ? (
            entry.source === "punch" ? "Clocked in/out" : "Entered by hand"
          ) : (
            <PunchLocationLine location={entry.location} ended={entry.endedAt !== null} />
          )}
        </span>

        <OfflineProvenance entry={entry} />
      </span>

      <span className="time-queue__hours">
        {/* A submitted row always has hours; the null case belongs to an open shift,
            which never reaches this queue. Rendered defensively anyway rather than as
            "0.00", which would be a number nobody claimed. */}
        {entry.hours === null ? "—" : entry.hours.toFixed(2)}
        <span className="time-queue__hours-unit"> hrs</span>

        {/*
          THE BREAK, AND THE WORD "NOT DEDUCTED" IS THE POINT.

          A farmer reading "8.50 hrs" over "45 min break" would reasonably assume one had
          been taken out of the other. It has not (0020): `hours` is the clocked duration and
          the break is a separate fact the worker reported, so whatever rule this business
          applies is still the farmer's to apply. Saying so on the row is cheaper than a
          payroll argument later.

          ⚠️ NOTHING HERE CLAIMS A RULE. Not "required", not "compliant" — this product has
          made no determination about meal-break law and must not read as though it had.

          Rendered only when a break was reported. Null means the worker was not asked or did
          not say, and "no break" printed on every manual row would be a claim the data does
          not support — a manual row cannot carry one at all.
        */}
        {entry.breakMinutes !== null && (
          <span className="time-queue__break" data-state="reported">
            {entry.breakMinutes} min break — not deducted
          </span>
        )}

        {/* ef-023 — the split, read off `entry.allocations` rather than inferred: which
            job codes this day's hours were assigned to, and how much went to each.
            Absence is deliberate, same rule OfflineProvenance/worker-note follow — an
            unallocated row renders nothing extra here rather than "0.00 unallocated". */}
        {isAllocated && (
          <span className="time-queue__allocations" data-state="allocated">
            {entry.allocations.map((allocation, i) => (
              <span key={allocation.jobCodeId}>
                {i > 0 && " · "}
                {allocation.hours.toFixed(2)} {allocation.name}
              </span>
            ))}
          </span>
        )}
      </span>

      {/*
        WHAT THE WORKER SAID, read BEFORE the buttons — same placement rule as SignedChip
        above, and for the same reason: a note the farmer reads after deciding is a note
        that changed nothing. It sits after the hours it explains and before the controls
        that act on them.

        UNLIKE SignedChip, ABSENCE IS NOT RENDERED. The distinction is deliberate. "Not
        signed" is a finding — a worker who did not attest to their own hours is
        information the farmer needs. "No note" is the ordinary day, and a "No note" line
        on every row would be noise on the one screen that has to stay scannable, and would
        train the eye to skip exactly the place a real note appears.
      */}
      {entry.workerNote !== null && entry.workerNote !== "" && (
        <span className="time-queue__worker-note" data-state="present">
          <span className="time-queue__worker-note-caption">
            {entry.employeeName} says
          </span>
          {/* Rendered as text, never as markup. It is free text a worker typed on a phone
              and this is the owner's screen. */}
          <span className="time-queue__worker-note-body">{entry.workerNote}</span>
        </span>
      )}

      {/*
        ef-037b (ADR 0009 §4) — "the approval queue goes from three buttons to one." Approve
        is the only action visible before the row's own menu opens; Reject and Allocate sit
        behind `RowActions`'s "More actions" disclosure. `RowActions` itself knows nothing
        about time entries — closing it (trigger toggle, Escape, or an outside click) is
        the one moment this row resets whichever of the two sub-forms below was left open,
        so reopening the menu never resumes mid-reject or mid-split from a farmer's last
        visit to a DIFFERENT row.
      */}
      <span className="time-queue__actions">
        <button
          type="button"
          className="btn btn--primary time-queue__btn"
          disabled={disabled}
          onClick={() => void onDecide(entry.id, true)}
        >
          {busy ? "…" : "Approve"}
        </button>

        <RowActions
          onOpenChange={(open) => {
            if (!open) {
              onCancelReject();
              setAllocateOpen(false);
            }
          }}
        >
          {rejecting ? (
            <span className="time-queue__reject-form">
              <label className="time-queue__note-label">
                <span className="time-queue__note-caption">Why not? The employee sees this.</span>
                <input
                  className="time-queue__note"
                  type="text"
                  value={note}
                  autoFocus
                  maxLength={200}
                  placeholder="e.g. put these on the trucking side"
                  onChange={(e) => setNote(e.target.value)}
                />
              </label>
              <span className="time-queue__reject-actions">
                <button
                  type="button"
                  className="btn btn--danger time-queue__btn"
                  disabled={!canReject || disabled}
                  onClick={() => void onDecide(entry.id, false, note.trim())}
                >
                  {busy ? "…" : "Reject"}
                </button>
                <button
                  type="button"
                  className="btn btn--ghost time-queue__btn"
                  disabled={disabled}
                  onClick={onCancelReject}
                >
                  Cancel
                </button>
              </span>
            </span>
          ) : (
            <>
              <span className="time-queue__menu-actions">
                <button
                  type="button"
                  className="btn btn--ghost time-queue__btn"
                  disabled={disabled}
                  onClick={onStartReject}
                >
                  Reject
                </button>
                {/* ef-023 — assign this day's hours to the farm's own job codes. */}
                <button
                  type="button"
                  className="btn btn--ghost time-queue__btn"
                  aria-expanded={allocateOpen}
                  onClick={() => setAllocateOpen((v) => !v)}
                >
                  Allocate
                </button>
              </span>
              {allocateOpen && (
                <AllocatePanel
                  entry={entry}
                  jobCodes={jobCodes}
                  onSaved={async () => {
                    setAllocateOpen(false);
                    await onAllocated();
                  }}
                />
              )}
            </>
          )}
        </RowActions>
      </span>
    </li>
  );
}

/** One line of the Allocate panel's own local, per-row form state — never the wire shape
 *  (`AllocationItem`), which needs both fields filled and validated first. */
interface AllocateLine {
  jobCodeId: string;
  hours: string;
}

function blankAllocateLine(): AllocateLine {
  return { jobCodeId: "", hours: "" };
}

/** Whether a line is unused, fully filled, or has one field set without the other — the
 *  third state is what disables Save without silently dropping the half-typed line. */
function lineState(line: AllocateLine): "empty" | "complete" | "partial" {
  const hasCode = line.jobCodeId !== "";
  const hasHours = line.hours.trim() !== "";
  if (!hasCode && !hasHours) return "empty";
  if (hasCode && hasHours) return "complete";
  return "partial";
}

/**
 * ef-023 — pick job codes, enter hours, watch the sum against the row's own total live,
 * and save the split with `PUT /time/{id}/allocations`.
 *
 * A FIXED TWO-LINE FORM, not an "add another line" list. The job plan's own worked
 * example splits one day two ways; two fixed lines is the smallest control that covers
 * it without building an unbounded-rows editor this job never asked for. A line left
 * entirely blank is simply not sent — see `rows` below — so a one-code, one-line
 * allocation works the same form without a second control.
 *
 * SUMS AND SHOWS THE WORKING, not just the total: `4.00 + 3.50 = 7.50 of 8.00 hours —
 * 0.50 remaining` names every figure a farmer would otherwise have to do the subtraction
 * on themselves, and is exactly what disables Save until it is true.
 */
function AllocatePanel({
  entry,
  jobCodes,
  onSaved,
}: {
  entry: TimeEntry;
  jobCodes: JobCode[];
  onSaved: () => Promise<void>;
}) {
  const [line1, setLine1] = useState<AllocateLine>(blankAllocateLine);
  const [line2, setLine2] = useState<AllocateLine>(blankAllocateLine);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const jobCodeId1 = useId();
  const jobCodeId2 = useId();
  const hoursId1 = useId();
  const hoursId2 = useId();

  const lines = [line1, line2];
  const states = lines.map(lineState);
  const anyPartial = states.includes("partial");

  const target = Math.round((entry.hours ?? 0) * 100) / 100;
  const parsedHours = lines.map((l) => (l.hours.trim() === "" ? null : Number(l.hours)));
  const filledHours = parsedHours.filter((h): h is number => h !== null && !Number.isNaN(h));
  const rawSum = filledHours.reduce((a, b) => a + b, 0);
  const sum = Math.round(rawSum * 100) / 100;
  const diff = Math.round((target - sum) * 100) / 100;
  const isShort = Math.abs(diff) > 0.004;

  const completeRows = lines
    .map((l, i) => ({ line: l, hours: parsedHours[i], complete: states[i] === "complete" }))
    .filter((r): r is { line: AllocateLine; hours: number; complete: true } => r.complete && r.hours !== null);
  const hasDuplicateCode =
    new Set(completeRows.map((r) => r.line.jobCodeId)).size !== completeRows.length;
  const allHoursPositive = completeRows.every((r) => r.hours > 0);

  const canSave =
    !anyPartial &&
    completeRows.length > 0 &&
    !hasDuplicateCode &&
    allHoursPositive &&
    !isShort &&
    !saving;

  const statusText =
    filledHours.length > 1
      ? `${filledHours.map((h) => h.toFixed(2)).join(" + ")} = ${sum.toFixed(2)} of ${target.toFixed(2)} hours`
      : `${sum.toFixed(2)} of ${target.toFixed(2)} hours`;

  async function save(): Promise<void> {
    setSaving(true);
    setError(null);
    try {
      await demoApi.putTimeAllocations(entry.id, {
        allocations: completeRows.map((r) => ({ jobCodeId: r.line.jobCodeId, hours: r.hours })),
      });
      await onSaved();
    } catch (err) {
      setError(problemMessage(err, "Could not save this split."));
    } finally {
      setSaving(false);
    }
  }

  return (
    <fieldset className="time-queue__allocate-panel" aria-label="Allocate hours">
      <div className="time-queue__allocate-line">
        <label htmlFor={jobCodeId1}>Job code 1</label>
        <select
          id={jobCodeId1}
          value={line1.jobCodeId}
          onChange={(e) => setLine1({ ...line1, jobCodeId: e.target.value })}
        >
          <option value="">Choose a job code</option>
          {jobCodes.map((jc) => (
            <option key={jc.id} value={jc.id}>
              {jc.name}
            </option>
          ))}
        </select>
        <label htmlFor={hoursId1}>Hours 1</label>
        <input
          id={hoursId1}
          type="text"
          inputMode="decimal"
          value={line1.hours}
          onChange={(e) => setLine1({ ...line1, hours: e.target.value })}
        />
      </div>

      <div className="time-queue__allocate-line">
        <label htmlFor={jobCodeId2}>Job code 2</label>
        <select
          id={jobCodeId2}
          value={line2.jobCodeId}
          onChange={(e) => setLine2({ ...line2, jobCodeId: e.target.value })}
        >
          <option value="">Choose a job code</option>
          {jobCodes.map((jc) => (
            <option key={jc.id} value={jc.id}>
              {jc.name}
            </option>
          ))}
        </select>
        <label htmlFor={hoursId2}>Hours 2</label>
        <input
          id={hoursId2}
          type="text"
          inputMode="decimal"
          value={line2.hours}
          onChange={(e) => setLine2({ ...line2, hours: e.target.value })}
        />
      </div>

      {/* role="status" — the live sum is exactly the ground truth Save's disabled state
          reads, so a screen-reader user gets the same running total a sighted farmer
          watches update as they type (ui-breadcrumbs §1). */}
      <p role="status" className="time-queue__allocate-status">
        {statusText}
        {isShort
          ? diff > 0
            ? ` — ${diff.toFixed(2)} remaining`
            : ` — ${Math.abs(diff).toFixed(2)} over`
          : " allocated"}
      </p>

      {error && (
        <p role="alert" className="time-queue__allocate-error">
          {error}
        </p>
      )}

      <button
        type="button"
        className="btn btn--primary time-queue__btn"
        disabled={!canSave}
        onClick={() => void save()}
      >
        {saving ? "Saving…" : "Save split"}
      </button>
    </fieldset>
  );
}
